import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import StickyBox from 'react-sticky-box';
import Breadcrumb from '../Breadcrumb';
import Sidebar from '../Sidebar';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { ToastConfirm, ToastError, Alerte_Warning } from '../../../ui/AlertesUi';
import { Api } from '../../auth/Configs';
import { PLAN_FOLDERS, PLAN_META_LIST } from '../../auth/urlParameters';
import ServiceMultiSelect from './ServiceMultiSelect';
import './CreateUserAccount.css';

const roleOptions = [
  {
    key: 'archiviste',
    label: 'Archiviste',
    role: 'ROLE_EDITED',
    details: ['Gère plans/dossiers', 'Valide conservation', 'Supervise indexation'],
  },
  {
    key: 'assistant',
    label: 'Assistant',
    role: 'ROLE_ASSIST',
    details: ['Dépose et met à jour dans ses dossiers autorisés'],
  },
  {
    key: 'lecteur',
    label: 'Lecteur',
    role: 'ROLE_USER',
    details: ['Lecture seule', 'Sans export si non autorisé'],
  },
  {
    key: 'admin',
    label: 'Admin',
    role: 'ROLE_ADMIN',
    details: ['Gestion comptes', 'Rôles', 'Paramètres globaux', 'Audit'],
  },
];

const wizardSteps = [
  { id: 1, label: 'Identité' },
  { id: 2, label: 'Accès & responsabilités' },
  { id: 3, label: 'Récapitulatif' },
];

const defaultAccount = {
  nom: '',
  prenoms: '',
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  role: 'assistant',
};

const flattenServices = (nodes, depth = 0) =>
  (nodes || []).reduce((acc, node) => {
    acc.push({
      id: node.id,
      label: `${'-- '.repeat(depth)}${node.code} - ${node.title}`,
    });
    if (node.children && node.children.length > 0) {
      acc.push(...flattenServices(node.children, depth + 1));
    }
    return acc;
  }, []);

const flattenFolders = (nodes, depth = 0) =>
  (nodes || []).reduce((acc, node) => {
    acc.push({
      id: node.id,
      planId: node.planId || null,
      label: `${'-- '.repeat(depth)}${node.reference} - ${node.title}`,
    });
    if (node.children && node.children.length > 0) {
      acc.push(...flattenFolders(node.children, depth + 1));
    }
    return acc;
  }, []);

const generatePassword = (length = 12) => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%';
  const values = new Uint32Array(length);
  const cryptoObj = window.crypto || window.msCrypto;
  if (cryptoObj && cryptoObj.getRandomValues) {
    cryptoObj.getRandomValues(values);
    return Array.from(values, (value) => chars[value % chars.length]).join('');
  }
  return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

const CreateUserAccount = () => {
  const session = useSelector((state) => state.sessionAuth);
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [account, setAccount] = useState(defaultAccount);
  const [saving, setSaving] = useState(false);
  const [warning, setWarning] = useState('');
  const [serviceIds, setServiceIds] = useState([]);
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [createdSnapshot, setCreatedSnapshot] = useState(null);
  const [copyStatus, setCopyStatus] = useState('');
  const credentialsRef = useRef(null);
  const [plans, setPlans] = useState([]);
  const [folders, setFolders] = useState([]);
  const [accessFilters, setAccessFilters] = useState({
    planIds: [],
    folderIds: [],
    maxConfidentialite: '',
    allowExport: true,
  });

  const selectedRole = useMemo(
    () => roleOptions.find((option) => option.key === account.role) || roleOptions[1],
    [account.role]
  );

  const authHeaders = useMemo(
    () => ({
      headers: { Authorization: 'Bearer ' + session.token },
    }),
    [session.token]
  );

  const fetchServices = useCallback(() => {
    setLoadingServices(true);
    axios
      .get(`${Api}/services`, authHeaders)
      .then((res) => {
        const flat = flattenServices(res.data?.services || []);
        setServices(flat);
      })
      .catch(() => setServices([]))
      .finally(() => setLoadingServices(false));
  }, [authHeaders]);

  const fetchPlans = useCallback(() => {
    axios
      .get(PLAN_META_LIST(), authHeaders)
      .then((res) => {
        setPlans(res.data?.plans || []);
      })
      .catch(() => setPlans([]));
  }, [authHeaders]);

  const fetchFolders = useCallback(() => {
    axios
      .get(PLAN_FOLDERS(), authHeaders)
      .then((res) => {
        const flat = flattenFolders(res.data?.folders || []);
        setFolders(flat);
      })
      .catch(() => setFolders([]));
  }, [authHeaders]);

  useEffect(() => {
    fetchServices();
    fetchPlans();
    fetchFolders();
  }, [fetchServices, fetchPlans, fetchFolders]);

  const isDisabled = useMemo(() => {
    if (!account.nom || !account.prenoms || !account.username) return true;
    if (account.nom.length < 2 || account.prenoms.length < 2) return true;
    if (account.username.length < 3) return true;
    if (account.password.length < 8) return true;
    if (account.password !== account.confirmPassword) return true;
    return false;
  }, [account]);

  const serviceLabel = useMemo(() => {
    const labels = services.filter((service) => serviceIds.includes(Number(service.id))).map((service) => service.label.replace(/^(-- )+/, ''));
    return labels.length ? labels.join(' · ') : 'Aucun';
  }, [serviceIds, services]);

  const summaryData = useMemo(() => {
    if (createdSnapshot) {
      return createdSnapshot;
    }
    return {
      nom: account.nom,
      prenoms: account.prenoms,
      username: account.username,
      email: account.email,
      serviceLabel,
      roleLabel: selectedRole.label,
      accessFilters,
    };
  }, [createdSnapshot, account, serviceLabel, selectedRole.label, accessFilters]);

  const planCount = summaryData.accessFilters?.planIds?.length || 0;
  const folderCount = summaryData.accessFilters?.folderIds?.length || 0;
  const planSummary = planCount > 0 ? `${planCount} plan(s) sélectionné(s)` : 'Tous les plans';
  const folderSummary =
    folderCount > 0
      ? `${folderCount} dossier(s) sélectionné(s)`
      : planCount > 0
        ? 'Tous les dossiers des plans sélectionnés'
        : 'Tous les dossiers';
  const confidentialitySummary =
    summaryData.accessFilters?.maxConfidentialite || 'Sans limite';
  const exportSummary =
    summaryData.accessFilters?.allowExport === false ? 'Non' : 'Oui';

  const filteredFolders = useMemo(() => {
    if (!accessFilters.planIds || accessFilters.planIds.length === 0) {
      return folders;
    }
    const allowedPlans = accessFilters.planIds.map(Number);
    return folders.filter((folder) => folder.planId && allowedPlans.includes(Number(folder.planId)));
  }, [accessFilters.planIds, folders]);

  useEffect(() => {
    if (accessFilters.planIds.length === 0 && accessFilters.folderIds.length > 0) {
      setAccessFilters((prev) => ({ ...prev, folderIds: [] }));
    }
  }, [accessFilters.planIds, accessFilters.folderIds]);

  useEffect(() => {
    if (!accessFilters.folderIds || accessFilters.folderIds.length === 0) {
      return;
    }
    if (!accessFilters.planIds || accessFilters.planIds.length === 0) {
      return;
    }
    const allowedIds = new Set(filteredFolders.map((folder) => folder.id));
    const filteredSelection = accessFilters.folderIds.filter((id) => allowedIds.has(Number(id)));
    if (filteredSelection.length !== accessFilters.folderIds.length) {
      setAccessFilters((prev) => ({ ...prev, folderIds: filteredSelection }));
    }
  }, [accessFilters.folderIds, accessFilters.planIds, filteredFolders]);

  const handleGeneratePassword = () => {
    const password = generatePassword();
    setAccount((prev) => ({ ...prev, password, confirmPassword: password }));
  };

  const resetWizard = () => {
    setAccount(defaultAccount);
    setServiceIds([]);
    setAccessFilters({ planIds: [], folderIds: [], maxConfidentialite: '', allowExport: true });
    setCreatedCredentials(null);
    setCreatedSnapshot(null);
    setCopyStatus('');
    setWarning('');
    setCurrentStep(1);
  };

  const handleRoleChange = (value) => {
    setAccount((prev) => ({ ...prev, role: value }));
    if (value === 'lecteur') {
      setAccessFilters((prev) => ({ ...prev, allowExport: false }));
    }
  };

  const validateStep1 = () => {
    if (!account.nom || account.nom.trim().length < 2) {
      return 'Nom requis (2 caractères minimum).';
    }
    if (!account.prenoms || account.prenoms.trim().length < 2) {
      return 'Prénoms requis (2 caractères minimum).';
    }
    if (!account.username || account.username.trim().length < 3) {
      return 'Nom d\'utilisateur requis (3 caractères minimum).';
    }
    if (account.password.length < 8) {
      return 'Le mot de passe doit contenir au moins 8 caractères.';
    }
    if (account.password !== account.confirmPassword) {
      return 'Les deux mots de passe ne sont pas identiques.';
    }
    return '';
  };

  const handleNext = () => {
    if (currentStep === 1) {
      const error = validateStep1();
      if (error) {
        setWarning(error);
        return;
      }
    }
    setWarning('');
    setCurrentStep((prev) => Math.min(prev + 1, 3));
  };

  const handleBack = () => {
    setWarning('');
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const credentialsText = useMemo(() => {
    if (!createdCredentials) return '';
    return `Identifiant: ${createdCredentials.username}\nEmail: ${createdCredentials.email}\nMot de passe: ${createdCredentials.password}`;
  }, [createdCredentials]);

  const copyCredentials = async () => {
    if (!credentialsText) return;
    try {
      await navigator.clipboard.writeText(credentialsText);
      setCopyStatus('Identifiants copies.');
    } catch (err) {
      if (credentialsRef.current) {
        credentialsRef.current.select();
        document.execCommand('copy');
        setCopyStatus('Identifiants copies.');
      } else {
        setCopyStatus('Copie impossible, veuillez copier manuellement.');
      }
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setWarning('');

    if (currentStep < 3) {
      handleNext();
      return;
    }

    if (account.password !== account.confirmPassword) {
      setWarning('Les deux mots de passe ne sont pas identiques.');
      return;
    }

    if (account.password.length < 8) {
      setWarning('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nom: account.nom,
        prenoms: account.prenoms,
        username: account.username,
        email: account.email.trim() || null,
        password: account.password,
        useruuid: uuidv4(),
        roles: [selectedRole.role],
        serviceIds,
        accessFilters: {
          planIds: accessFilters.planIds || [],
          folderIds: accessFilters.planIds?.length ? accessFilters.folderIds || [] : [],
          maxConfidentialite: accessFilters.maxConfidentialite || null,
          allowExport: accessFilters.allowExport !== false,
        },
      };

      const res = await axios.post(`${Api}/accounts/signup_in`, payload, authHeaders);
      if (res.status === 200 && res.data?.status === 400) {
        ToastError(res.data?.message || 'Erreur creation compte');
        return;
      }
      ToastConfirm('Compte cree avec succes');
      setCreatedCredentials({
        username: account.username,
        email: account.email,
        password: account.password,
      });
      setCreatedSnapshot({
        nom: account.nom,
        prenoms: account.prenoms,
        username: account.username,
        email: account.email,
        serviceLabel,
        roleLabel: selectedRole.label,
        accessFilters,
      });
      setCurrentStep(3);
      setCopyStatus('');
      setAccount(defaultAccount);
      setServiceIds([]);
      setAccessFilters({ planIds: [], folderIds: [], maxConfidentialite: '', allowExport: true });
    } catch (err) {
      ToastError(err.response?.data?.message || err.message || 'Erreur de création de compte');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumb title="Administration" submenu="Création utilisateur" />
      <div className="content user-create-page">
        <div className="container">
          <div className="row">
            <div className="col-md-5 col-lg-4 col-xl-3 theiaStickySidebar">
              <StickyBox offsetTop={20} offsetBottom={20}>
                <Sidebar />
              </StickyBox>
            </div>
            <div className="col-md-7 col-lg-8 col-xl-9">
              <div className="row app_items">
                <div className="col-12">
                  <div className="user-create-header">
                    <div>
                      <span className="user-create-eyebrow">ADMINISTRATION</span>
                      <h1>Créer un utilisateur</h1>
                      <p>Renseignez l’identité, définissez les accès, puis vérifiez les informations.</p>
                    </div>
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => navigate('/apps/in/users-accounts/page/1')}
                    >
                      <i className="ri-arrow-left-line" aria-hidden="true"></i> Retour à la liste
                    </button>
                  </div>
                </div>

                <div className="col-12">
                  <form onSubmit={onSubmit} className="user-create-form">
                    <ol className="user-create-steps" aria-label="Étapes de création">
                      {wizardSteps.map((step) => (
                        <li
                          key={step.id}
                          className={`user-create-step ${
                            currentStep === step.id
                              ? 'is-current'
                              : currentStep > step.id
                                ? 'is-complete'
                                : 'is-pending'
                          }`}
                          aria-current={currentStep === step.id ? 'step' : undefined}
                        >
                          <span className="user-create-step-number" aria-hidden="true">
                            {currentStep > step.id ? <i className="ri-check-line"></i> : step.id}
                          </span>
                          <span className="user-create-step-label">{step.label}</span>
                        </li>
                      ))}
                    </ol>

                    {currentStep === 1 && (
                      <div className="card user-create-card">
                      <div className="card-body">
                        <h2 className="user-create-card-title">Identité</h2>
                        <p className="user-create-card-intro">Informations de base et connexion du nouvel utilisateur.</p>
                        <div className="row form-row">
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label>Nom</label>
                              <input
                                type="text"
                                className="form-control"
                                value={account.nom}
                                onChange={(e) => setAccount({ ...account, nom: e.target.value })}
                                required
                              />
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label>Prénoms</label>
                              <input
                                type="text"
                                className="form-control"
                                value={account.prenoms}
                                onChange={(e) => setAccount({ ...account, prenoms: e.target.value })}
                                required
                              />
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label>Nom d'utilisateur</label>
                              <input
                                type="text"
                                className="form-control"
                                value={account.username}
                                onChange={(e) => setAccount({ ...account, username: e.target.value })}
                                required
                              />
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label>Email (optionnel)</label>
                              <input
                                type="email"
                                className="form-control"
                                value={account.email}
                                onChange={(e) => setAccount({ ...account, email: e.target.value })}
                              />
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <ServiceMultiSelect services={services} selected={serviceIds} onChange={setServiceIds} disabled={loadingServices} />
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label>Mot de passe</label>
                              <div className="user-create-password-input">
                                <input
                                  type="password"
                                  className="form-control"
                                  value={account.password}
                                  onChange={(e) => setAccount({ ...account, password: e.target.value })}
                                  required
                                />
                                <button
                                  className="btn btn-outline-primary"
                                  type="button"
                                  onClick={handleGeneratePassword}
                                  disabled={saving}
                                >
                                  Générer
                                </button>
                              </div>
                              <small className="form-text text-muted">8 caractères minimum.</small>
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label>Confirmer le mot de passe</label>
                              <input
                                type="password"
                                className="form-control"
                                value={account.confirmPassword}
                                onChange={(e) =>
                                  setAccount({ ...account, confirmPassword: e.target.value })
                                }
                                required
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    )}

                    {currentStep === 2 && (
                      <div className="card user-create-card">
                      <div className="card-body">
                        <h2 className="user-create-card-title">Accès & responsabilités</h2>
                        <p className="user-create-card-intro">Choisissez le rôle et limitez les documents accessibles si nécessaire.</p>
                        <div className="row">
                          {roleOptions.map((option) => (
                            <div className="col-12 col-lg-6 mb-3" key={option.key}>
                              <label
                                className={`user-create-role-option${account.role === option.key ? ' is-selected' : ''}`}
                              >
                                <div className="form-check mb-2">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="role"
                                    value={option.key}
                                    checked={account.role === option.key}
                                    onChange={(e) => handleRoleChange(e.target.value)}
                                  />
                                  <span className="ms-2 fw-bold">{option.label}</span>
                                </div>
                                <ul className="small text-muted mb-0">
                                  {option.details.map((detail) => (
                                    <li key={detail}>{detail}</li>
                                  ))}
                                </ul>
                              </label>
                            </div>
                          ))}
                        </div>
                        <hr />
                        <div className="row">
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label>Plans autorisés</label>
                              <select
                                multiple
                                className="form-control"
                                value={accessFilters.planIds.map(String)}
                                onChange={(e) => {
                                  const values = Array.from(e.target.selectedOptions).map((opt) =>
                                    Number(opt.value)
                                  );
                                  setAccessFilters((prev) => ({ ...prev, planIds: values }));
                                }}
                              >
                                {plans.map((plan) => (
                                  <option key={plan.id} value={plan.id}>
                                    {plan.code} - {plan.title}
                                  </option>
                                ))}
                              </select>
                              <small className="form-text text-muted">
                                Laissez vide pour autoriser tous les plans.
                              </small>
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label>Dossiers autorisés</label>
                              <select
                                multiple
                                className="form-control"
                                value={accessFilters.folderIds.map(String)}
                                onChange={(e) => {
                                  const values = Array.from(e.target.selectedOptions).map((opt) =>
                                    Number(opt.value)
                                  );
                                  setAccessFilters((prev) => ({ ...prev, folderIds: values }));
                                }}
                                disabled={!accessFilters.planIds || accessFilters.planIds.length === 0}
                              >
                                {filteredFolders.map((folder) => (
                                  <option key={folder.id} value={folder.id}>
                                    {folder.label}
                                  </option>
                                ))}
                              </select>
                              <small className="form-text text-muted">
                                Sans plan sélectionné, tous les dossiers et sous-dossiers sont autorisés.
                                Sinon, laissez vide pour autoriser tous les dossiers et sous-dossiers des plans sélectionnés, dans le périmètre des autres restrictions du compte.
                                Si vous sélectionnez des dossiers, l'accès est limité à ceux-ci et à leurs sous-dossiers.
                              </small>
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label>Niveau de confidentialité max</label>
                              <select
                                className="form-control"
                                value={accessFilters.maxConfidentialite}
                                onChange={(e) =>
                                  setAccessFilters((prev) => ({
                                    ...prev,
                                    maxConfidentialite: e.target.value,
                                  }))
                                }
                              >
                                <option value="">Sans limite</option>
                                <option value="Interne">Interne</option>
                                <option value="Restreint">Restreint</option>
                                <option value="Confidentiel">Confidentiel</option>
                                <option value="Tres Confidentiel">Très confidentiel</option>
                              </select>
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label className="d-flex align-items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={accessFilters.allowExport !== false}
                                  onChange={(e) =>
                                    setAccessFilters((prev) => ({
                                      ...prev,
                                      allowExport: e.target.checked,
                                    }))
                                  }
                                />
                                Autoriser l'export (téléchargement)
                              </label>
                              <small className="form-text text-muted">
                                Désactivez pour bloquer l'export des archives.
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    )}

                    {currentStep === 3 && (
                      <div className="card user-create-card">
                        <div className="card-body">
                          <h2 className="user-create-card-title">Récapitulatif</h2>
                          <p className="user-create-card-intro">Vérifiez les informations avant de créer le compte.</p>
                          <div className="row user-create-summary">
                            <div className="col-12 col-md-6">
                              <div className="mb-2">
                                <strong>Nom:</strong> {summaryData.nom || '-'}
                              </div>
                              <div className="mb-2">
                                <strong>Prénoms:</strong> {summaryData.prenoms || '-'}
                              </div>
                              <div className="mb-2">
                                <strong>Nom d'utilisateur:</strong> {summaryData.username || '-'}
                              </div>
                              <div className="mb-2">
                                <strong>Email:</strong> {summaryData.email || '-'}
                              </div>
                              <div className="mb-2">
                                <strong>Service:</strong> {summaryData.serviceLabel || 'Aucun'}
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="mb-2">
                                <strong>Rôle:</strong> {summaryData.roleLabel || '-'}
                              </div>
                              <div className="mb-2">
                                <strong>Plans:</strong> {planSummary}
                              </div>
                              <div className="mb-2">
                                <strong>Dossiers:</strong> {folderSummary}
                              </div>
                              <div className="mb-2">
                                <strong>Confidentialité max:</strong> {confidentialitySummary}
                              </div>
                              <div className="mb-2">
                                <strong>Export autorisé:</strong> {exportSummary}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {warning && <div className="mt-3">{Alerte_Warning(warning)}</div>}

                    {currentStep === 3 && createdCredentials && (
                      <div className="card user-create-card mt-3">
                        <div className="card-body">
                          <h5 className="card-title">Identifiants de connexion</h5>
                          <p className="text-muted">
                            Copiez ces identifiants et transmettez-les à l'utilisateur.
                          </p>
                          <textarea
                            ref={credentialsRef}
                            className="form-control"
                            rows="3"
                            readOnly
                            value={credentialsText}
                          />
                          <div className="d-flex align-items-center gap-2 mt-2">
                            <button type="button" className="btn btn-outline-primary" onClick={copyCredentials}>
                              Copier les identifiants
                            </button>
                            {copyStatus && <span className="text-success small">{copyStatus}</span>}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="user-create-actions">
                      {currentStep > 1 && (
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={handleBack}
                          disabled={saving}
                        >
                          Retour
                        </button>
                      )}
                      {currentStep < 3 && (
                        <button
                          className="btn btn-primary"
                          type="button"
                          onClick={handleNext}
                          disabled={saving}
                        >
                          Suivant
                        </button>
                      )}
                      {currentStep === 3 && (
                        <button className="btn btn-primary" type="submit" disabled={isDisabled || saving}>
                          {saving ? 'Création en cours...' : 'Créer l\'utilisateur'}
                        </button>
                      )}
                      {currentStep === 3 && createdCredentials && (
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={resetWizard}
                          disabled={saving}
                        >
                          Nouvel utilisateur
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default CreateUserAccount;
